import { Box, CircularProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import ChooseFood from "assets/illustrations/chooseFoodImage.svg";
import ProcessingOrderImage from "assets/illustrations/processingOrderImage.svg";
import OrderStep2Arrow from "assets/OrderStep2Arrow.svg";
import { BlurredBackground } from "components/primitives/BlurredBackground";
import { DrawerSubHeaderText } from "components/primitives/DrawerSubHeaderText";
import { DrawerToggleIndicator } from "components/primitives/DrawerToggleIndicator";
import { HeaderText } from "components/primitives/HeaderText";
import { StyledBottomMessage } from "components/primitives/StyledBottomMessage";
import { SwipeableArea } from "components/primitives/SwipeableArea";
import { useMixpanelTrackers } from "hooks/useMixpanelTrackers";
import { useOpenDoorAlarm } from "hooks/useOpenDoorAlarm";
import { useAppSelector } from "hooks/utilsHooks/storeHooks";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
	selectActiveLocalTransaction,
	selectDiscountCodeState,
	selectOrderDishTypes,
	selectOrderSummary,
	selectTransactionDishes,
} from "slices/transactionSlice";
import { TransactionState } from "types";
import { DRAWER_TOTAL_HEIGHT, ORDER_STEP_3_SMALL_ORDER_LIMIT } from "utils/designTokens";
import { OpenedDoorWarningBox } from "./components/OpenedDoorWarningBox";
import { OrderDishesList } from "./components/OrderDishesList";
import { OrderListDiscountCodeItem } from "./components/OrderListDiscountCodeItem";
import { OrderDishesWrapper, OrderItemBlockWrapper } from "./components/orderStepDrawerStyles";
import { StyledDishTypeList } from "./components/StyledDishTypeList";
import type { BaseOrderStepProps } from "./OrderStep2";

const BODY_TOP_OFFSET = 220;
const ACTION_BAR_CLEARANCE = 120;
const BODY_CONTENT_HEIGHT = DRAWER_TOTAL_HEIGHT - BODY_TOP_OFFSET - ACTION_BAR_CLEARANCE;

const Body = styled(Box)(() => ({
	width: "100%",
	// Capped at the viewport so the list, not the discount code row, shrinks above the action bar.
	maxHeight: `calc(100vh - ${BODY_TOP_OFFSET}px)`,
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	padding: `0 0 ${ACTION_BAR_CLEARANCE}px 0`,
	img: {
		marginTop: 16,
	},
}));

const StyledEmptyBasketColumn = styled(Box)({
	width: "100%",
	height: BODY_CONTENT_HEIGHT,
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	gap: 8,
	"& > svg": {
		flex: "1 1 auto",
		minHeight: 0,
		// Let the viewBox aspect ratio derive the width from the flexed height instead of the 640px attribute.
		width: "auto",
	},
});

const TakeYourMealsHeader = () => {
	const { t } = useTranslation();
	return (
		<>
			<HeaderText variant="h3">{t("order.step3.header")}</HeaderText>
			<OrderStep2Arrow />
			<DrawerSubHeaderText>{t("order.step3.subheader")}</DrawerSubHeaderText>
		</>
	);
};

const StockCheckHeader = () => {
	const { t } = useTranslation();

	return (
		<>
			<HeaderText variant="h3" hasBorder={true}>
				{t("order.step4.header")}
			</HeaderText>
			<DrawerSubHeaderText>{t("order.step4.subheader")}</DrawerSubHeaderText>
		</>
	);
};

const StockCheckDrawerMessage = () => {
	const { t } = useTranslation();
	return (
		<StyledBottomMessage data-testid="stock-check">
			<Typography variant="promoHeader">{t("order.step4.header")}...</Typography>
			<CircularProgress sx={{ margin: "auto", color: "#00396c" }} size="75px" thickness={5} />
		</StyledBottomMessage>
	);
};

export const OrderStep3 = ({ open, toggleDrawer }: BaseOrderStepProps) => {
	const transaction = useAppSelector(selectActiveLocalTransaction);
	const transactionState = transaction?.state;
	const isStockCheck = transactionState === TransactionState.StockCheck;
	const { trackStockCheckStarted, trackOrderStep3Screen } = useMixpanelTrackers();
	const { resetCountdown, displayOpenedDoorWarning } = useOpenDoorAlarm(transaction?.type, transactionState);

	// The step-3 lifecycle events carry the transaction id so their $insert_id is order-keyed
	// and a remount/ghost re-run rides the sent-events registry (JID-2347); wait for the
	// transaction rather than emitting an unkeyed event.
	const transactionId = transaction?.id;
	useEffect(() => {
		if (!transactionId) {
			return;
		}
		trackOrderStep3Screen(transactionId);
	}, [trackOrderStep3Screen, transactionId]);

	// Dismissing the warning/alarm when the transaction leaves Active is owned by useOpenDoorAlarm now
	// (it stops, rather than restarting the countdown). This effect only handles the StockCheck analytics.
	useEffect(() => {
		if (transactionId && transactionState === TransactionState.StockCheck) {
			trackStockCheckStarted(transactionId);
		}
	}, [transactionState, trackStockCheckStarted, transactionId]);

	useEffect(() => {
		if (displayOpenedDoorWarning) {
			toggleDrawer(true);
		}
	}, [displayOpenedDoorWarning, toggleDrawer]);

	return (
		<>
			<SwipeableArea orderstep={3} open={open}>
				{!open && <BlurredBackground orderStep={3} open={open} />}
				<DrawerToggleIndicator open={open} toggleDrawer={toggleDrawer} />
				<Box sx={{ width: "100%", display: "flex", flexDirection: "column", paddingTop: 2 }}>
					{isStockCheck ? <StockCheckHeader /> : <TakeYourMealsHeader />}
				</Box>
			</SwipeableArea>
			<Body
				sx={{
					...(open && {
						marginTop: `${BODY_TOP_OFFSET}px`,
					}),
				}}
			>
				<OrderStep3Body displayOpenedDoorWarning={displayOpenedDoorWarning} resetCountdown={resetCountdown} />
			</Body>
		</>
	);
};

interface OrderStep3BodyProps {
	displayOpenedDoorWarning: boolean;
	resetCountdown: () => void;
}

const OrderStep3Body = (props: OrderStep3BodyProps) => {
	const { t } = useTranslation();
	const orderDishes = useAppSelector(selectTransactionDishes);
	const orderDishTypes = useAppSelector(selectOrderDishTypes);
	const orderSummary = useAppSelector(selectOrderSummary);
	const transaction = useAppSelector(selectActiveLocalTransaction);
	const discountCode = useAppSelector(selectDiscountCodeState);
	const transactionState = transaction?.state;
	const { displayOpenedDoorWarning, resetCountdown } = props;

	if (orderDishes.length === 0 && transactionState === TransactionState.Active) {
		return (
			<>
				<StyledEmptyBasketColumn>
					<ChooseFood />
					<OrderListDiscountCodeItem discountCode={discountCode} isOrderFinished={false} />
				</StyledEmptyBasketColumn>
				{displayOpenedDoorWarning && <OpenedDoorWarningBox resetCountdown={resetCountdown} />}
			</>
		);
	}

	if (orderDishes.length > 0) {
		return (
			<>
				<StyledDishTypeList
					// The aggregate count: OrderDishesList renders discounted types alongside full-price ones.
					isLargeOrder={(orderSummary?.orderDishTypesCount ?? 0) > ORDER_STEP_3_SMALL_ORDER_LIMIT}
					data-testid="dish-list"
				>
					<OrderItemBlockWrapper>
						{orderDishTypes && (
							<OrderDishesWrapper>
								<OrderDishesList smallOrderLimit={ORDER_STEP_3_SMALL_ORDER_LIMIT} fitContent={discountCode !== null} />
							</OrderDishesWrapper>
						)}
					</OrderItemBlockWrapper>
				</StyledDishTypeList>
				{/* Outside the masked scroll list, so dishes never scroll under the translucent row.
				    Dropped during the stock check — the message below occupies the same band. */}
				{transactionState === TransactionState.Active && (
					<OrderListDiscountCodeItem discountCode={discountCode} isOrderFinished={false} />
				)}
				{transactionState === TransactionState.StockCheck && <StockCheckDrawerMessage />}
				{displayOpenedDoorWarning && <OpenedDoorWarningBox resetCountdown={resetCountdown} />}
			</>
		);
	}

	if (transactionState === TransactionState.StockCheck) {
		return (
			<>
				<ProcessingOrderImage
					style={{
						position: "absolute",
						top: "165px",
					}}
				/>
				<StyledBottomMessage data-testid="stock-check">
					<Typography variant="promoHeader">{t("order.step4.header")}...</Typography>
					<CircularProgress sx={{ margin: "auto", color: "#00396c" }} size="75px" thickness={5} />
				</StyledBottomMessage>
			</>
		);
	}
};
